// Adaptateur : convertit les sélections visuelles CotIdel en input pour le moteur NGAP

import { analyzeCotation, AnalyseInput, AnalyseResult, ForceFlags } from '@/pages/analysis/engine';
import { OPTIONS } from './data/careData';

export interface PeriodSelection {
  matin: string[];
  midi: string[];
  soir: string[];
}

export interface BSIState {
  bsiType: string | null;
}

export interface CotIciInput {
  periods: PeriodSelection;
  optionIds: string[];
  bsi: BSIState;
}

function buildDescription(input: CotIciInput): string {
  const parts: string[] = [];

  // Période Matin
  if (input.periods.matin.length > 0) {
    parts.push(`Matin : ${input.periods.matin.join(', ')}.`);
  }

  // Période Midi
  if (input.periods.midi.length > 0) {
    parts.push(`Midi : ${input.periods.midi.join(', ')}.`);
  }

  // Période Soir
  if (input.periods.soir.length > 0) {
    parts.push(`Soir : ${input.periods.soir.join(', ')}.`);
  }

  // BSI
  if (input.bsi.bsiType) {
    parts.push(input.bsi.bsiType);
  }

  // Options activées — injecter les keywords dans la description pour la détection texte fallback
  const optionMap = new Map(OPTIONS.map((o) => [o.id, o]));
  const activeKeywords = input.optionIds
    .map((id) => optionMap.get(id)?.keyword)
    .filter(Boolean) as string[];

  if (activeKeywords.length > 0) {
    parts.push(`Options : ${activeKeywords.join(', ')}.`);
  }

  return parts.join(' ');
}

function buildForceFlags(optionIds: string[]): ForceFlags {
  const flags: ForceFlags = {};

  const mapping: Record<string, keyof ForceFlags> = {
    'opt-mie': 'enfant',
    'opt-nuit': 'nuit',
    'opt-nuit-pro': 'nuitProfonde',
    'opt-dimanche': 'dimanche',
    'opt-ferie': 'jourFerie',
    'opt-urgence': 'urgence',
    'opt-prem-visite': 'premiereVisite',
    'opt-immunodeprime': 'immunodeprime',
  };

  optionIds.forEach((id) => {
    const flagKey = mapping[id];
    if (flagKey) {
      flags[flagKey] = true;
    }
  });

  return flags;
}

export function runCotIciAnalysis(input: CotIciInput): AnalyseResult {
  const description = buildDescription(input);

  if (!description.trim()) {
    return {
      cotations: [],
      cumuls: [],
      majorations: [],
      limites: [],
      total: { sousTotalActes: '0,00 €', totalMajorations: '+ 0,00 €', totalGeneral: '0,00 €' },
      risque: { niveau: 'Non évaluable', score: 0, couleur: 'gray', details: ['Aucun soin sélectionné.'] },
      sources: [],
      avertissements: ['Sélectionnez au moins un soin dans l\'une des périodes pour lancer l\'analyse.'],
      resumeAnalyse: 'Aucun soin sélectionné.',
      actesInjection: 0,
      actesPansement: 0,
      contradictions: [],
    };
  }

  const analyseInput: AnalyseInput = {
    description,
    periods: input.periods,
    forceFlags: buildForceFlags(input.optionIds),
  };
  return analyzeCotation(analyseInput);
}

// Génère une explication pas à pas basée sur le résultat
export interface StepExplanation {
  step: number;
  title: string;
  detail: string;
  icon: string;
}

export function generateExplanation(result: AnalyseResult): StepExplanation[] {
  const steps: StepExplanation[] = [];

  // Étape 1 : Identification des actes
  if (result.cotations.length > 0) {
    const actList = result.cotations.map((c) => `${c.code} — ${c.label}`).join('\n');
    steps.push({
      step: 1,
      title: 'Identification des actes',
      detail: `${result.cotations.length} acte(s) identifié(s) selon la NGAP 2026 :\n${actList}`,
      icon: 'ri-search-eye-line',
    });
  }

  // Étape 2 : Règle de cumul
  const hasCumul = result.cotations.some((c) => c.tauxFacturation !== 100);
  if (hasCumul) {
    const nb100 = result.cotations.filter((c) => c.tauxFacturation === 100).length;
    const nb50 = result.cotations.filter((c) => c.tauxFacturation === 50).length;
    const nb0 = result.cotations.filter((c) => c.tauxFacturation === 0).length;
    steps.push({
      step: 2,
      title: 'Règle de cumul (Art. 11B)',
      detail: `Application de la règle 100%/50%/gratuit : ${nb100} acte(s) à 100%, ${nb50} à 50%, ${nb0} gratuit(s). Les actes sont triés du coefficient le plus élevé au plus faible, puis le 1er est facturé à 100%, le 2e à 50%, les suivants sont gratuits.`,
      icon: 'ri-git-merge-line',
    });
  } else {
    steps.push({
      step: 2,
      title: 'Règle de cumul (Art. 11B)',
      detail: 'Tous les actes sont facturés à taux plein. Aucun cumul 100/50/0 n\'a été nécessaire.',
      icon: 'ri-git-merge-line',
    });
  }

  // Étape 3 : Majorations
  const applicableMaj = result.majorations.filter((m) => m.applicable);
  if (applicableMaj.length > 0) {
    const majList = applicableMaj.map((m) => `${m.code} : ${m.montant} — ${m.raison}`).join('\n');
    steps.push({
      step: 3,
      title: 'Majorations applicables',
      detail: `${applicableMaj.length} majoration(s) appliquée(s) :\n${majList}`,
      icon: 'ri-add-circle-line',
    });
  } else {
    steps.push({
      step: 3,
      title: 'Majorations',
      detail: 'Aucune majoration n\'est applicable dans ce cas.',
      icon: 'ri-add-circle-line',
    });
  }

  // Étape 4 : Conversion AMX si BSI
  const hasAMX = result.cotations.some((c) => c.code.startsWith('AMX'));
  if (hasAMX) {
    steps.push({
      step: 4,
      title: 'Conversion AMX (contexte BSI)',
      detail: 'Un contexte BSI a été détecté. Les actes non dérogatoires ont été convertis en AMX à 50% du coefficient. Les actes dérogatoires (pansements lourds, perfusions, ponction veineuse) restent à taux plein.',
      icon: 'ri-exchange-line',
    });
  }

  // Étape 5 : Contradictions
  if (result.contradictions.length > 0) {
    const ctrList = result.contradictions.map((c) => `• ${c.titre} : ${c.suggestion}`).join('\n');
    steps.push({
      step: steps.length + 1,
      title: 'Points d\'attention',
      detail: `${result.contradictions.length} point(s) à vérifier :\n${ctrList}`,
      icon: 'ri-alert-line',
    });
  }

  // Étape finale : Total
  steps.push({
    step: steps.length + 1,
    title: 'Total de la cotation',
    detail: `Sous-total actes : ${result.total.sousTotalActes}\nMajorations : ${result.total.totalMajorations}\nTotal général : ${result.total.totalGeneral}`,
    icon: 'ri-money-euro-circle-line',
  });

  return steps;
}

// Génère la liste des règles appliquées
export interface AppliedRule {
  article: string;
  description: string;
  type: 'ngap' | 'avenant' | 'exception' | 'cumul' | 'incompatibilite';
}

export function getAppliedRules(result: AnalyseResult): AppliedRule[] {
  const rules: AppliedRule[] = [];

  // Toujours présent : règle de cumul
  rules.push({
    article: 'Art. 11B NGAP',
    description: 'Règle de cumul : 1er acte à 100%, 2e à 50%, suivants gratuits. Classement par coefficient décroissant.',
    type: 'ngap',
  });

  // Articles spéciaux
  const has5bis = result.cotations.some((c) => c.article.includes('5bis'));
  if (has5bis) {
    rules.push({
      article: 'Art. 5bis NGAP',
      description: 'Patient diabétique insulino-dépendant : les actes liés au diabète se cumulent à taux plein entre eux, sans application de l\'Article 11B.',
      type: 'exception',
    });
    rules.push({
      article: 'Art. 5bis — Bon à savoir',
      description: 'L\'article 5 bis ne s\'applique qu\'aux patients insulino-dépendants, diagnostiqués par le médecin traitant. Les soins sans lien avec le diabète doivent être décotés (100/50/0).',
      type: 'exception',
    });
  }

  const has5ter = result.cotations.some((c) => c.article.includes('5ter'));
  if (has5ter) {
    rules.push({
      article: 'Art. 5ter NGAP',
      description: 'Surveillance post-hospitalisation (AMI 5,8) : max 15 séances, nécessite formation IDEL spécifique.',
      type: 'exception',
    });
  }

  const hasArt7 = result.cotations.some((c) => c.article.includes('Art. 7'));
  if (hasArt7) {
    rules.push({
      article: 'Art. 7 NGAP',
      description: 'Soins postopératoires (RAAC) : les actes se cumulent à taux plein entre eux.',
      type: 'exception',
    });
  }

  // Majorations
  const hasMAU = result.majorations.find((m) => m.code === 'MAU');
  if (hasMAU?.applicable) {
    rules.push({
      article: 'Avenant n°6',
      description: `MAU (${hasMAU.montant}) : majoration acte unique pour AMI 1 ou AMI 1,5 isolé.`,
      type: 'avenant',
    });
  }

  const hasMCI = result.majorations.find((m) => m.code === 'MCI');
  if (hasMCI?.applicable) {
    rules.push({
      article: 'Avenant n°6',
      description: `MCI (${hasMCI.montant}) : majoration de coordination pour soins complexes (une fois par passage).`,
      type: 'avenant',
    });
  }

  // Conversion AMX
  const hasAMX = result.cotations.some((c) => c.code.startsWith('AMX'));
  if (hasAMX) {
    rules.push({
      article: 'Art. A12 Titre XVI',
      description: 'Conversion AMX à 50% sous BSI, sauf actes dérogatoires (pansements lourds, perfusions, ponction veineuse).',
      type: 'ngap',
    });
  }

  // Cumuls
  const hasCumuls = result.cumuls.some((c) => c.acte2 !== '—');
  if (hasCumuls) {
    const cumulOk = result.cumuls.filter((c) => c.compatible && c.acte2 !== '—');
    cumulOk.forEach((c) => {
      rules.push({
        article: c.article,
        description: `Cumul ${c.acte1} + ${c.acte2} : ${c.regle}`,
        type: 'cumul',
      });
    });
  }

  // Incompatibilités
  const incompatibles = result.cumuls.filter((c) => !c.compatible);
  incompatibles.forEach((c) => {
    rules.push({
      article: c.article,
      description: `Incompatibilité ${c.acte1} + ${c.acte2} : ${c.regle}`,
      type: 'incompatibilite',
    });
  });

  // Date de mise à jour
  rules.push({
    article: 'Mise à jour',
    description: 'Tarifs NGAP 2026 — Métropole. Dernière mise à jour : août 2026.',
    type: 'ngap',
  });

  return rules;
}